import {
  Box,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { passwordSchema, confirmPasswordSchema } from "../../common/passwordPolicy";
import { FieldTypes } from "../Forms/FieldTypes";
import { FormField } from "../Forms/FormField";
import Form from "../Forms/Form";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Loading from "../Widgets/Loading";
import InvitationUnavailable from "./InvitationUnavailable";
import { isModuleEnabled } from "../../modules/activeModules";
import UnauthenticatedPageLayout from "../shared/UnauthenticatedPageLayout";
import SessionService from "../../services/SessionService";
import { useCerberus } from "../../hooks/useCerberus";
import { useSnackbar } from "notistack";
import apiClient from "../../api/apiClient";
import { useBranding } from "../../branding/BrandingProvider";

function getFields() {
  const fields: FormField[] = [
    {
      id: "password",
      text: "New Password",
      password: true,
      type: FieldTypes.Text,
      yupSchema: passwordSchema(),
    },
    {
      id: "confirmPassword",
      text: "Confirm New Password",
      password: true,
      type: FieldTypes.Text,
      yupSchema: confirmPasswordSchema("password"),
    },
  ];

  return fields;
}

const AcceptInvitationNewPatientSelfSignup = () => {
  const { invitationId } = useParams();
  const brand = useBranding();
  const [invitation, setInvitation] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [linkUnavailable, setLinkUnavailable] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSession, setMfaSession] = useState("");
  const [mfaError, setMfaError] = useState<string>();
  const sessionService = SessionService.getInstance();
  const { refreshUserData } = useCerberus();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: Record<string, unknown>) => {
    const newUser = {
      ...values,
    };

    try {
      // apiClient carries `withCredentials`, and THAT IS A FIX, not a refactor: this call was made
      // with bare axios and no credentials at all, so the browser dropped the Set-Cookie the server
      // sends on a successful accept (the API is a different origin). The patient's account was
      // created, `setSession` recorded an expiry, and the screen navigated to "/" — with no session
      // cookie behind it. AcceptInvitation.tsx had exactly this bug and fixed it in place; this
      // screen is its copy and never got the fix. It also picks up the `application-name` header the
      // server requires on every mutating request (AUD-9).
      const result = await apiClient.post(
        `/invitation/${invitationId}/accept`,
        newUser,
        // The screen renders the failure itself (errorMessage), so no global dialog on top of it.
        { suppressErrorDialog: true },
      );

      if (result.status === 200) {
        // Check for success/failure
        if (result.data.success === false) {
          setErrorMessage(result.data.error);
        } else {
          // Check if MFA is required
          if (result.data.requiresMfa) {
            setMfaRequired(true);
            setMfaSession(result.data.mfaSession);
            enqueueSnackbar("Please enter the verification code sent to your email", {
              variant: "info",
            });
          } else if (result.data.success) {
            // Auto-login: the server set the session cookie on this response;
            // we just record the expiry locally for countdowns.
            sessionService.setSession(result.data.expiresIn);
            await refreshUserData();
            enqueueSnackbar("Account created successfully! You are now logged in.", {
              variant: "success",
            });
            // Client-side, in the same commit as the identity update. A full page load
            // (window.location) left the app signed in at /i/p/<id> for the length of the
            // reload — a route the authenticated tree does not have, so the user watched
            // the 404 page until the new document arrived. `replace` also drops the spent
            // invitation from history.
            navigate("/", { replace: true });
          } else {
            // This case should ideally not be reached if the invitation is accepted successfully
            // but as a fallback, we can set a message or redirect to dashboard.
            // For now, we'll just set a message.
            setErrorMessage("Unexpected response from server.");
          }
        }
      } else {
        alert("Something went wrong! Please try again later.");
      }
    } catch (error) {
      console.log(error);
      setErrorMessage("An error occurred while accepting the invitation. Please try again.");
    }
  };

  const handleMfaSubmit = async (values: Record<string, unknown>) => {
    try {
      // Same fix as the accept call above: this is the response that carries the session cookie for
      // an MFA'd signup, and it was being sent without credentials.
      const response = await apiClient.post(
        `/auth/verify-mfa`,
        {
          username: invitation.email,
          code: values.code,
          session: mfaSession,
        },
        // A wrong code is a 400 shown inline as mfaError; the modal would be a duplicate.
        { suppressErrorDialog: true },
      );

      if (response.data.success) {
        sessionService.setSession(response.data.expiresIn);
        await refreshUserData();
        enqueueSnackbar("Account created successfully! You are now logged in.", {
          variant: "success",
        });
        // Same reasoning as the accept path above: client-side, replacing the invite URL.
        navigate("/", { replace: true });
      } else {
        setMfaError(response.data.message || "Verification failed");
      }
    } catch (error) {
      let message = "Verification failed";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      setMfaError(message);
    }
  };

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        // Read, so AUD-9 does not apply — but there is no reason for this screen to keep its own
        // transport for one GET. The failure is rendered inline, so no dialog.
        const result = await apiClient.get(`/invitation/${invitationId}`, {
          suppressErrorDialog: true,
        });

        setInvitation(result.data);
      } catch {
        // The link itself is unusable (used, expired or never issued) — see InvitationUnavailable.
        setLinkUnavailable(true);
      }

      setLoading(false);
    };

    fetchInvitation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <UnauthenticatedPageLayout hideAppBar>
      <Container maxWidth="xl">
        <Box sx={{ py: 8 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 4,
            }}
          >
            <img
              src={brand.logo}
              alt={`${brand.displayName} logo`}
              style={{ height: "80px" }}
            />
          </Box>
          {linkUnavailable && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 },
                width: "100%",
                maxWidth: 600,
                mx: "auto",
                borderRadius: 2,
                boxShadow: (theme) => theme.palette.brand.shadow.raised,
              }}
            >
              <InvitationUnavailable canRequestNew={isModuleEnabled("patient-self-signup")} />
            </Paper>
          )}
          {!linkUnavailable && errorMessage && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                width: "100%",
                maxWidth: 600,
                mx: "auto",
                borderRadius: 2,
                boxShadow: (theme) => theme.palette.brand.shadow.raised,
              }}
            >
              <Box sx={{ display: "block" }}>
                <Typography
                  variant="h4"
                  align="center"
                  sx={{ mb: 2, color: "primary.main", fontWeight: 700 }}
                >
                  {errorMessage}
                </Typography>
              </Box>
            </Paper>
          )}
          {!linkUnavailable && !errorMessage && !mfaRequired && (
            <>
              <Typography
                variant="h2"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  mb: 4,
                  textAlign: "center",
                }}
              >
                Continue Registration
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  width: "100%",
                  maxWidth: 600,
                  mx: "auto",
                  borderRadius: 2,
                  boxShadow: (theme) => theme.palette.brand.shadow.raised,
                }}
              >
                <Box sx={{ display: "block" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.primary",
                      mb: 3,
                      textAlign: "center",
                      maxWidth: 600,
                      mx: "auto",
                      fontWeight: 500,
                    }}
                  >
                    Hello {invitation.name}, please set your password to
                    continue registration.
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  <Form
                    onSubmit={handleSubmit}
                    fields={getFields()}
                    submitButtonText="Submit"
                  />
                </Box>
              </Paper>
            </>
          )}
          {mfaRequired && (
            <>
              <Typography
                variant="h2"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  mb: 4,
                  textAlign: "center",
                }}
              >
                Verify Your Email
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  width: "100%",
                  maxWidth: 600,
                  mx: "auto",
                  borderRadius: 2,
                  boxShadow: (theme) => theme.palette.brand.shadow.raised,
                }}
              >
                <Box sx={{ display: "block" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.primary",
                      mb: 3,
                      textAlign: "center",
                      maxWidth: 600,
                      mx: "auto",
                      fontWeight: 500,
                    }}
                  >
                    Please enter the verification code sent to your email.
                  </Typography>

                  {mfaError && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "error.main",
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      {mfaError}
                    </Typography>
                  )}

                  <Divider sx={{ my: 3 }} />

                  <Form
                    onSubmit={handleMfaSubmit}
                    fields={[
                      {
                        id: "code",
                        text: "Verification Code",
                        type: FieldTypes.Text,
                        yupSchema: Yup.string()
                          .required("Required")
                          .matches(/^\d+$/, "Code must contain only numbers")
                          .length(6, "Code must be 6 digits"),
                      },
                    ]}
                    submitButtonText="Verify"
                  />
                </Box>
              </Paper>
            </>
          )}
        </Box>
      </Container>
    </UnauthenticatedPageLayout>
  );
};

export default AcceptInvitationNewPatientSelfSignup;
